class DoseExpectationsTimeline extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('[data-track]');
    this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartScroll = 0;
    this.hasMoved = false;
    this.lineAnimated = false;
    this.lineObserver = null;

    this.init();
  }

  init() {
    // Desktop line animation: trigger once when the section scrolls into
    // view. Only the class is toggled — fill width/duration and dot color
    // delays all live in CSS.
    this.lineObserver = new IntersectionObserver((entries) => this.handleLineIntersect(entries), {
      threshold: 0.3,
    });
    this.lineObserver.observe(this);

    if (!this.track || this.slides.length === 0) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.goToSlide(Number(tab.dataset.slideIndex)));
    });

    this.observer = new IntersectionObserver((entries) => this.handleIntersect(entries), {
      root: this.track,
      threshold: 0.6,
    });
    this.slides.forEach((slide) => this.observer.observe(slide));

    this.track.addEventListener('pointerdown', (event) => this.startDrag(event));
    this.track.addEventListener('pointermove', (event) => this.duringDrag(event));
    this.track.addEventListener('pointerup', (event) => this.endDrag(event));
    this.track.addEventListener('pointercancel', (event) => this.endDrag(event));
    this.track.addEventListener('click', (event) => this.suppressClickAfterDrag(event), true);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  handleLineIntersect(entries) {
    for (const entry of entries) {
      if (entry.isIntersecting && !this.lineAnimated) {
        this.lineAnimated = true;
        this.classList.add('is-animating');
        this.lineObserver?.disconnect();
        this.lineObserver = null;
        return;
      }
    }
  }

  disconnectedCallback() {
    this.lineObserver?.disconnect();
    this.lineObserver = null;
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.setActiveTab(Number(entry.target.dataset.slideIndex));
      }
    });
  }

  setActiveTab(index) {
    this.tabs.forEach((tab) => {
      const isActive = Number(tab.dataset.slideIndex) === index;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive);
    });
  }

  goToSlide(index) {
    const slide = this.slides[index];
    if (!slide) return;
    slide.scrollIntoView({ behavior: 'smooth', inline: 'end', block: 'nearest' });
  }

  startDrag(event) {
    if (event.pointerType === 'touch') return;
    this.isDragging = true;
    this.hasMoved = false;
    this.dragStartX = event.clientX;
    this.dragStartScroll = this.track.scrollLeft;
    this.track.setPointerCapture(event.pointerId);
    this.track.classList.add('is-dragging');
  }

  duringDrag(event) {
    if (!this.isDragging) return;
    const delta = event.clientX - this.dragStartX;
    if (Math.abs(delta) > 3) this.hasMoved = true;
    this.track.scrollLeft = this.dragStartScroll - delta;
  }

  endDrag(event) {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.track.classList.remove('is-dragging');
    if (event && event.pointerId !== undefined && this.track.hasPointerCapture(event.pointerId)) {
      this.track.releasePointerCapture(event.pointerId);
    }
  }

  suppressClickAfterDrag(event) {
    if (this.hasMoved) {
      event.preventDefault();
      event.stopPropagation();
      this.hasMoved = false;
    }
  }
}

if (!customElements.get('dose-expectations-timeline')) {
  customElements.define('dose-expectations-timeline', DoseExpectationsTimeline);
}
